import { FilterIcon, XIcon } from "lucide-react";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Badge } from "./badge";
import { ScrollArea } from "./scroll-area";
import { Separator } from "./separator";
import { cn } from "@util/tailwind";
import { useEffect, useState } from "react";
import { JobsFilterState } from "@type/job";
import { getJobFilter } from "@service/job";
import { SubCompany } from "@type/company";

interface Props {
    filters: JobsFilterState
    onFiltersChange: (filters: JobsFilterState) => void;
    className?: string;
    isInActiveTab?: boolean;
}

export default function JobFilterV2({ className, filters, isInActiveTab, onFiltersChange }: Props) {
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
    const [countryOptions, setCountryOptions] = useState<string[]>([]);
    const [companyOptions, setCompanyOptions] = useState<string[]>([]);
    const [departmentOptions, setDepartmentOptions] = useState<string[]>([]);
    const [filterOptions, setFilterOptions] = useState<JobsFilterState>(filters);
    const [listDepartmentsByCompany, setListDepartmentsByCompany] = useState<SubCompany[]>([]);
    const jobStatusOptions = isInActiveTab ? [
        { value: "Paused", label: "Paused" },
        { value: "Closed", label: "Closed" },
    ] : [
        { value: "Published", label: "Published" },
    ];
    const workArrangementOptions = [
        { value: "Remote", label: "Remote" },
        { value: "Onsite", label: "Onsite" },
        { value: "Hybrid", label: "Hybrid" },
    ];
    const commitmentTypeOptions = [
        { value: "Full time", label: "Full time" },
        { value: "Contract", label: "Contract" },
        { value: "Internship", label: "Internship" },
    ];

    useEffect(() => {
        if (isFilterPanelOpen) {
            getJobFilter().then((resp) => {
                if (resp.status === 200) {
                    setCountryOptions(resp.data.data.locations || []);
                    setCompanyOptions((resp.data.data.companies || []).map((company: any) => company.name));
                    setListDepartmentsByCompany(resp.data.data.companies || []);

                    const departments: string[] = [];
                    (resp.data.data.companies || []).filter((company: any) => company.is_main).forEach((company: any) => {
                        (company.departments || []).forEach((dept: any) => {
                            if (!departments.includes(dept)) {
                                departments.push(dept);
                            }
                        })
                    })
                    setDepartmentOptions(departments);
                }
            })
        }
    }, [isFilterPanelOpen])

    useEffect(() => {
        setFilterOptions(filters);
    }, [filters])

    const handleClearAll = () => {
        const clearedFilters = {
            jobStatus: [],
            workArrangement: [],
            commitmentType: [],
            country: [],
            company: [],
            department: [],
        };
        setFilterOptions(clearedFilters);
        setDepartmentOptions([]);
    };

    const handleClearSection = (section: string) => {
        const updatedFilters = { ...filterOptions, [section]: [] };
        setFilterOptions(updatedFilters);

        if (section === 'company') {
            const departments: string[] = [];
            (listDepartmentsByCompany || []).filter((company: any) => company.is_main).forEach((company: any) => {
                (company.departments || []).forEach((dept: any) => {
                    if (!departments.includes(dept)) {
                        departments.push(dept);
                    }
                })
            })
            setDepartmentOptions(departments);
        }
    };

    const handleUpdateFilters = (section: keyof JobsFilterState, value: string) => {
        const updatedFilters = { ...filterOptions };
        if (updatedFilters[section].includes(value)) {
            updatedFilters[section] = updatedFilters[section].filter((item) => item !== value);

            if (section === 'company') {
                const departments: string[] = [];
                if (updatedFilters[section].length === 0) {
                    (listDepartmentsByCompany || []).filter((company: any) => company.is_main).forEach((company: any) => {
                        (company.departments || []).forEach((dept: any) => {
                            if (!departments.includes(dept)) {
                                departments.push(dept);
                            }
                        })
                    })
                } else {
                    updatedFilters[section].forEach((companyName: string) => {
                        const selectedCompany = listDepartmentsByCompany.find((company) => company.name === companyName);
                        if (selectedCompany) {
                            (selectedCompany.departments || []).forEach((dept: string) => {
                                if (!departments.includes(dept)) {
                                    departments.push(dept);
                                }
                            })
                        }
                    })
                }
                setDepartmentOptions(departments);
            }
        } else {
            updatedFilters[section].push(value);
            if (section === 'company') {
                const selectedDepartments = listDepartmentsByCompany.find((company) => company.name === value)?.departments || [];
                console.log("Selected Companies: ", selectedDepartments);
                const departments: string[] = [
                    ...departmentOptions,
                ];
                selectedDepartments.forEach((dept: string) => {
                    if (!departments.includes(dept)) {
                        departments.push(dept);
                    }
                })
                console.log("departments: ", departments);
                setDepartmentOptions(departments);
            }
        }
        setFilterOptions(updatedFilters);
    }

    const handleApplyFilters = () => {
        onFiltersChange(filterOptions);
        console.log("filters: ", filterOptions);
        setIsFilterPanelOpen(false);
    }

    const handleCancelFilters = () => {
        setFilterOptions(filters);
        setIsFilterPanelOpen(false);
    }

    const { jobStatus: _jobStatus, ...otherFilters } = filterOptions;
    const activeFilterCount = _jobStatus.filter(s => s !== "Published").length + Object.values(otherFilters).reduce((count, filterArray) => count + filterArray.length, 0);

    return (
        <div className={cn("flex items-center gap-2", className)}>
            {/* Unified Filter Panel - Popup */}
            <Popover open={isFilterPanelOpen} onOpenChange={setIsFilterPanelOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        className={cn(
                            "h-9",
                            isFilterPanelOpen &&
                            "border-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-600"
                        )}
                    >
                        <FilterIcon className="h-4 w-4 mr-2" />
                        Filters
                        {activeFilterCount > 0 && (
                            <Badge
                                variant="secondary"
                                className="ml-2 bg-blue-600 text-white hover:bg-blue-600 h-5 min-w-[10px] flex items-center justify-center px-2.5 rounded-md"
                            >
                                {activeFilterCount}
                            </Badge>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[480px] p-0" align="end">
                    <div className="p-4 border-b">
                        <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-sm">Filter Jobs by</h4>
                            {activeFilterCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleClearAll}
                                    className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                                >
                                    Clear all ({activeFilterCount})
                                </Button>
                            )}
                        </div>
                    </div>

                    <ScrollArea className="h-[500px]">
                        <div className="p-4 space-y-6">
                            {/* Status Section - Only show for Inactive Jobs tab */}
                            {isInActiveTab && (
                                <>
                                    <div className="space-y-3">
                                        <div className="flex items-center justify-between">
                                            <h4 className="font-semibold text-sm">Status</h4>
                                            {filterOptions.jobStatus.length > 0 && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleClearSection('status')}
                                                    className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {jobStatusOptions.map((option) => (
                                                <Button
                                                    key={option.value}
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => handleUpdateFilters('jobStatus', option.value)}
                                                    className={cn(
                                                        "h-8 rounded-full transition-colors",
                                                        filterOptions.jobStatus.includes(option.value)
                                                            ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                                            : "hover:border-blue-300"
                                                    )}
                                                >
                                                    {option.label}
                                                    {filterOptions.jobStatus.includes(option.value) && (
                                                        <XIcon className="ml-1.5 h-3 w-3" />
                                                    )}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>

                                    <Separator />
                                </>
                            )}

                            {/* Work Arrangement Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-sm">Work Arrangement</h4>
                                    {filterOptions.workArrangement.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleClearSection('workArrangement')}
                                            className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {workArrangementOptions.map((option) => (
                                        <Button
                                            key={option.value}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdateFilters('workArrangement', option.value)}
                                            className={cn(
                                                "h-8 rounded-full transition-colors",
                                                filterOptions.workArrangement.includes(option.value)
                                                    ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                                    : "hover:border-blue-300"
                                            )}
                                        >
                                            {option.label}
                                            {filterOptions.workArrangement.includes(option.value) && (
                                                <XIcon className="ml-1.5 h-3 w-3" />
                                            )}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <Separator />

                            {/* Commitment Type Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-sm">Commitment Type</h4>
                                    {filterOptions.commitmentType.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleClearSection('commitmentType')}
                                            className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {commitmentTypeOptions.map((option) => (
                                        <Button
                                            key={option.value}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdateFilters('commitmentType', option.value)}
                                            className={cn(
                                                "h-8 rounded-full transition-colors",
                                                filterOptions.commitmentType.includes(option.value)
                                                    ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                                    : "hover:border-blue-300"
                                            )}
                                        >
                                            {option.label}
                                            {filterOptions.commitmentType.includes(option.value) && (
                                                <XIcon className="ml-1.5 h-3 w-3" />
                                            )}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <Separator />

                            {/* Location Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-sm">Location</h4>
                                    {filterOptions.country.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleClearSection('country')}
                                            className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {countryOptions.map((option) => (
                                        <Button
                                            key={option}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdateFilters('country', option)}
                                            className={cn(
                                                "h-8 rounded-full transition-colors",
                                                filterOptions.country.includes(option)
                                                    ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                                    : "hover:border-blue-300"
                                            )}
                                        >
                                            {option}
                                            {filterOptions.country.includes(option) && (
                                                <XIcon className="ml-1.5 h-3 w-3" />
                                            )}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <Separator />

                            {/* Company Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-sm">Company</h4>
                                    {filterOptions.company.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleClearSection('company')}
                                            className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {companyOptions.map((option) => (
                                        <Button
                                            key={option}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdateFilters('company', option)}
                                            className={cn(
                                                "h-8 rounded-full transition-colors",
                                                filterOptions.company.includes(option)
                                                    ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                                    : "hover:border-blue-300"
                                            )}
                                        >
                                            {option}
                                            {filterOptions.company.includes(option) && (
                                                <XIcon className="ml-1.5 h-3 w-3" />
                                            )}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <Separator />

                            {/* Department Section */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <h4 className="font-semibold text-sm">Department</h4>
                                    {filterOptions.department.length > 0 && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleClearSection('department')}
                                            className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                                        >
                                            Clear
                                        </Button>
                                    )}
                                </div>
                                {filterOptions.company.length > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        Showing departments for: {filterOptions.company.join(", ")}
                                    </p>
                                )}
                                <div className="flex flex-wrap gap-2">
                                    {departmentOptions.map((dept) => (
                                        <Button
                                            key={dept}
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleUpdateFilters('department', dept)}
                                            className={cn(
                                                "h-8 rounded-full transition-colors",
                                                filterOptions.department.includes(dept)
                                                    ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                                    : "hover:border-blue-300"
                                            )}
                                        >
                                            {dept}
                                            {filterOptions.department.includes(dept) && (
                                                <XIcon className="ml-1.5 h-3 w-3" />
                                            )}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </ScrollArea>

                    <div className="p-4 border-t flex gap-2">
                        <Button
                            variant="outline"
                            onClick={handleCancelFilters}
                            className="flex-1"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApplyFilters}
                            className="flex-1 bg-blue-600 hover:bg-blue-700"
                        >
                            Apply
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    )
}